/* eslint-disable max-len */
import { useEffect, useState } from "react";
// @ts-ignore
import "./PaymentSection.scss";
import { Button } from "elements/button/Button";
import { SvgIcon } from "elements/svg-icon/svg-icon";
import { COLORS } from "models/colors";
import { VARIANTS } from "models/variants";
import { bemElement, bemModifier } from "utils/bem-class-names";
import {
  TBookAppointmentResponse,
  TCalculateAppointment,
  TCompany
} from "types";
import FormattedTextModal from "components/modals/formatted-text-modal/FormattedTextModal";
import { useCart } from "providers/cart-provider";
import { joinClassNames } from "utils/join-class-names";
import { ICreditCardInputValue } from "elements/credit-card-input/CreditCardInput";
import { CreditCard } from "elements/credit-card/CreditCard";
import { usePayment } from "hooks/use-payment";
import { useAxios } from "providers/axios";
import { Toast } from "elements/toast/Toast";
import { Desktop, Mobile } from "components/responsive/Responsive";
import { PAYMENT_TYPES } from "models/payment-types";
import { useNavigate } from "react-router-dom";
import { StripePaymentMethodFragment } from "hooks/use-payment/PaymentMethodsSection.generated";
import { APP_ROUTES } from "routes";
import { useUser } from "../../../providers/user";
import BookingConfirmationModal from "../../../components/modals/booking-confirmation-modal/BookingConfirmationModal";
import {
  getDate,
  getFormattedTime,
  getRelativeDateTime
} from "../../../utils/date-time";
import { usePeopleCount } from "../../../hooks/use-people-count";
import { inStorePaymentMethods } from "utils/giftCardAvailability";
import { AddToCalendarModal } from "../../../components/modals/add-to-calendar-modal/AddToCalendarModal";
import { GiftCardRedeem } from "components/gift-card-redeem/GiftCardRedeem";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { CARD_ELEMENT_OPTIONS } from "stripeConfig";
import { getRawPhoneNumber } from "../../../utils/getRawPhoneNumber";

/* eslint-disable no-console */
const baseClassName = "payment-section";
const bem = bemElement(baseClassName);
const defaultDescription =
  "Make sure that all your Reservation details are correct and complete the payment.";

export interface IPaymentSectionProps {
  description?: string;
}

const getRefundAndCancellationText = (company?: TCompany) => {
  if (!company) {
    return "";
  }

  if (company?.status === "LIMITED") {
    return `
    <p style="font-family: Inter, sans-serif; font-size: 14px; color: #302F2E">
      We will place a hold on your card at the time of booking, and the card will be charged 12 hours before the appointment’s scheduled start time. Change of plans? Cancel at least 12 hours in advance in order to avoid a nonrefundable charge. Failure to cancel within the allotted time frame will result in a charge of the full service total.
    </p>`;
  }

  switch (company.payment_type) {
    case PAYMENT_TYPES.PAY_FULL_UPFRONT:
      return `
    <h3 style="margin-bottom: 8px; color: #302F2E">Full Prepay</h3>
    <div>
      Take note! This salon requires full payment of the service total at the time of booking. Your card will be charged shortly. Change of plans? Just cancel or reschedule your Pointment at least ${company.late_cancellation_hours} hours before the start time in order to keep your salon credit. Failure to cancel within the allotted time frame will result in a loss of salon credit. 
    </div>`;
    case PAYMENT_TYPES.PAY_IN_STORE:
      return `
    <h3 style="margin-bottom: 8px; color: #302F2E">Pay in Store Option </h3>
    <div>
      Change of plans? Be sure to cancel your Pointment at least ${
        company.late_cancellation_hours
      } hours before the start time in order to avoid a cancellation fee. According to this salon's policy, late cancellations will be charged ${parseFloat(
        (company.late_cancellation_charge * 100).toFixed(2)
      )}% of the service total, and no shows will be charged ${parseFloat(
        (company.no_show_charge * 100).toFixed(2)
      )}%. 
    </div>`;
    default:
      return "";
  }
};

const EXPIRING_CARD_ERROR =
  "This card expires within 30 days and can't be used for this booking. Please use a different card.";

const isCardExpiringSoon = (expMonth: number, expYear: number): boolean => {
  const cardExpiresAt = new Date(expYear, expMonth, 1);
  const diffDays =
    (cardExpiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return diffDays <= 30;
};

// Cards are valid through the last day of their expiry month.
const isCardExpired = (expMonth: number, expYear: number): boolean =>
  new Date(expYear, expMonth, 1).getTime() <= Date.now();

const getAvailablePaymentType = (
  company?: TCompany,
  calculateAppointment?: TCalculateAppointment | null
) => {
  if (!calculateAppointment || !company) {
    return null;
  }

  let paymentTypeData = {
    title: "Pay in full",
    description: `${
      calculateAppointment.amount_paid_now_with_refunds > 0
        ? `$${calculateAppointment.amount_paid_now_with_refunds.toFixed(
            2
          )} in credits has been applied.`
        : ""
    } Payment of $${calculateAppointment.amount_paid_with_payment_method.toFixed(
      2
    )} is required to book.`,
    iconName: "other_full_payment",
    iconPosition: "middle"
  };

  switch (calculateAppointment.payment_type) {
    case PAYMENT_TYPES.PAY_IN_STORE:
      paymentTypeData = {
        title: "Pay in store",
        description: `Final payment of $${calculateAppointment.total_price?.toFixed(
          2
        )} at the time of your appointment by: ${inStorePaymentMethods(
          company
        )}. ${
          company.late_cancellation_charge > 0
            ? `Late cancellation fee of $${(
                company.late_cancellation_charge *
                (calculateAppointment.total_price || 0)
              ).toFixed(2)} would apply.`
            : ""
        }`,
        iconName: "other_store",
        iconPosition: "start"
      };
      break;
  }

  if (company?.status === "LIMITED") {
    paymentTypeData = {
      title: "Pay in full",
      description: calculateAppointment?.is_charged_immediately
        ? `A payment of $${calculateAppointment.amount_paid_with_payment_method.toFixed(
            2
          )} will be charged to your card now to secure your appointment. You are within the 12 hour cancellation window, so there will be no refund if you cannot make the appointment.`
        : `We will place a hold on your card at the time of booking, and a payment of $${calculateAppointment.amount_paid_with_payment_method.toFixed(
            2
          )} will be charged 12 hours before the appointment start time.`,
      iconName: "other_full_payment",
      iconPosition: "middle"
    };
  }

  return (
    <div
      className={bemModifier(bem("payment-type"), paymentTypeData.iconPosition)}
    >
      <div className={bem("payment-type-left")}>
        <SvgIcon name={paymentTypeData.iconName} />
      </div>
      <div className={bem("payment-type-right")}>
        <span>{paymentTypeData.title}</span>
        <span>{paymentTypeData.description}</span>
      </div>
    </div>
  );
};

export const PaymentSection = ({
  description = defaultDescription
}: IPaymentSectionProps) => {
  const {
    cart,
    bookedAppointmentResponse,
    getBookAppointmentBody,
    setBookedAppointmentResponse,
    resetEverything
  } = useCart();
  const { cards, addCard, changeDefaultPaymentMethod } = usePayment();
  const navigate = useNavigate();
  const { api } = useAxios();
  const { user } = useUser();
  const stripe = useStripe();
  const elements = useElements();

  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [showTextModal, setShowTextModal] = useState<boolean>(false);
  const [textModalData, setTextModalData] = useState<{
    title: string;
    text: string;
  }>();
  const [addNewCardField, setAddNewCardField] = useState<boolean>(false);
  const [newCard, setNewCard] = useState<ICreditCardInputValue | undefined>(
    undefined
  );
  const [payingLoading, setPayingLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isPaymentSuccess, setIsPaymentSuccess] = useState<boolean>(false);
  const [disabledOrderButton, setDisabledOrderButton] =
    useState<boolean>(false);
  const [calculateAppointment, setCalculateAppointment] =
    useState<TCalculateAppointment | null>(null);
  // Gift card codes the customer has applied, in the order they added them.
  // Held here rather than in the cart because they belong to this checkout: a
  // customer who backs out and rebuilds their booking should type them again
  // rather than silently spend a card they had forgotten about.
  const [giftCardCodes, setGiftCardCodes] = useState<string[]>([]);
  const [repricing, setRepricing] = useState<boolean>(false);
  // Whether a gift card can be spent here at all. Deliberately not
  // company.gift_cards_enabled: a salon that switches the feature off stops new
  // sales, but cards it already sold stay spendable, and gating on that flag
  // quietly took the online way to spend them away. The server answers "feature
  // on, or this company still has cards with money on them" - and always no
  // below the Full plan, where gift cards cannot be spent at all.
  const [giftCardsRedeemable, setGiftCardsRedeemable] =
    useState<boolean>(false);
  const [showAddToCalendarModal, setShowAddToCalendarModal] =
    useState<boolean>(false);
  const peopleCount = usePeopleCount(
    bookedAppointmentResponse?.createdAppointment
  );

  const selectPaymentMethodId = (card: StripePaymentMethodFragment) => {
    setPaymentMethodId(card.object_json.id);
    setAddNewCardField(false);
    setNewCard(undefined);

    // If the current default card is expired, the card the user switches to
    // becomes the new default (as long as it isn't expired itself). For
    // pay-in-store the card is kept on file for late-cancel/no-show fees, so
    // a card that expires within 30 days doesn't qualify as default either.
    const defaultCard = cards.find(
      (c: StripePaymentMethodFragment) => c.is_default
    );
    const disqualifiedForPayInStore =
      calculateAppointment?.payment_type === PAYMENT_TYPES.PAY_IN_STORE &&
      isCardExpiringSoon(card.exp_month, card.exp_year);
    if (
      defaultCard &&
      defaultCard.id !== card.id &&
      isCardExpired(defaultCard.exp_month, defaultCard.exp_year) &&
      !isCardExpired(card.exp_month, card.exp_year) &&
      !disqualifiedForPayInStore
    ) {
      changeDefaultPaymentMethod(card);
    }
  };

  const onAddCardClick = () => {
    setAddNewCardField(true);
    setPaymentMethodId(null);
  };

  const handlePayButtonClick = async (event: any) => {
    event.preventDefault();
    setPayingLoading(true);
    const enforceExpiryCheck =
      calculateAppointment?.payment_type !== PAYMENT_TYPES.PAY_FULL_UPFRONT;
    if (paymentMethodId) {
      if (enforceExpiryCheck) {
        const selectedCard = cards.find(
          (c: StripePaymentMethodFragment) =>
            c.object_json?.id === paymentMethodId
        );
        if (
          selectedCard &&
          isCardExpiringSoon(selectedCard.exp_month, selectedCard.exp_year)
        ) {
          setErrorMsg(EXPIRING_CARD_ERROR);
          setPayingLoading(false);
          return;
        }
      }
      bookAppointment(paymentMethodId);
    } else if (addNewCardField) {
      if (!stripe || !elements) {
        return;
      }

      const cardElement = elements.getElement(CardElement);

      if (cardElement) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name:
              user?.impersonate_name ||
              `${user?.first_name} ${user?.last_name}`,
            email: user?.impersonate_name ? "" : user?.email,
            phone: user?.impersonate_name
              ? ""
              : getRawPhoneNumber(user?.phone_number || "") || undefined
          }
        });

        if (error) {
          setErrorMsg(error?.message || "");
          setPayingLoading(false);
        } else if (
          enforceExpiryCheck &&
          paymentMethod?.card?.exp_month &&
          paymentMethod?.card?.exp_year &&
          isCardExpiringSoon(
            paymentMethod.card.exp_month,
            paymentMethod.card.exp_year
          )
        ) {
          setErrorMsg(EXPIRING_CARD_ERROR);
          setPayingLoading(false);
        } else {
          try {
            const newPaymentMethod = await addCard({
              paymentMethod: {
                id: paymentMethod?.id,
                brand: paymentMethod?.card?.brand,
                exp_month: paymentMethod?.card?.exp_month,
                exp_year: paymentMethod?.card?.exp_year,
                last4: paymentMethod?.card?.last4
              },
              is_default: cards.length === 0
            });
            setPaymentMethodId(
              JSON.parse(newPaymentMethod?.data?.object_json)?.id
            );

            if (newPaymentMethod?.data?.stripe_id) {
              bookAppointment(newPaymentMethod.data.stripe_id);
            }
          } catch (e) {
            setErrorMsg(e?.response?.data?.message || e || "");
            setPayingLoading(false);
          }
        }
      }
    } else if (
      user?.impersonate_name &&
      calculateAppointment?.payment_type === PAYMENT_TYPES.PAY_IN_STORE
    ) {
      bookAppointment(PAYMENT_TYPES.PAY_IN_STORE);
    } else {
      // TODO: add message for empty cards list
      setErrorMsg("Please select payment method or add new card");
      setPayingLoading(false);
    }
  };

  const bookAppointment = async (id: string) => {
    (async function () {
      try {
        const response: TBookAppointmentResponse = (await api
          .post(`/v1/book-appointment/`, {
            ...getBookAppointmentBody(),
            impersonate_id: user?.impersonate_name
              ? user?.impersonate_id
              : undefined,
            // The server re-validates and re-prices these; the amount it
            // charges is worked out from the codes, not from anything we
            // send it.
            gift_card_codes: giftCardCodes,
            payment_method_id: id
          })
          .then((r) => r.data)) as TBookAppointmentResponse;

        setBookedAppointmentResponse(response);
        setIsPaymentSuccess(true);
      } catch (err) {
        setErrorMsg(err.response.data.message);
      } finally {
        setPayingLoading(false);
      }
    })();
  };

  useEffect(() => {
    const locationId = cart.location?.id;

    if (!locationId) return;

    let cancelled = false;

    api
      .get("/v1/gift-cards/redeemable", { params: { location_id: locationId } })
      .then((response: { data: { redeemable?: boolean } }) => {
        if (!cancelled) {
          setGiftCardsRedeemable(Boolean(response.data?.redeemable));
        }
      })
      .catch(() => {
        // Hide the field rather than show one that may not work. Staff can
        // still take the card in the salon.
        if (!cancelled) setGiftCardsRedeemable(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, cart.location?.id]);

  useEffect(() => {
    if ((window as any).ApplePaySession) {
      console.log("Apple Pay is supported in this browser");
      // setPaymentMethods([applePay, mockDefaultCard]);
    } else {
      // setPaymentMethods([mockDefaultCard, mockDefaultCard1]);
    }
  }, []);

  useEffect(() => {
    if (cards && !newCard) {
      setPaymentMethodId(
        cards.find((card: StripePaymentMethodFragment) => card.is_default)
          ?.object_json?.id
      );
    }
  }, [cards, newCard]);

  useEffect(() => {
    // Once the booking has gone through this screen is finished with. Re-pricing
    // it would ask the server to validate gift cards that have just been spent,
    // and an "empty card" error would land on top of the confirmation.
    if (isPaymentSuccess) {
      return;
    }

    (async function () {
      setRepricing(true);
      try {
        const response: TCalculateAppointment = (await api
          .post(`/v1/calculate-appointment/`, {
            ...getBookAppointmentBody(),
            impersonate_id: user?.impersonate_name
              ? user?.impersonate_id
              : undefined,
            gift_card_codes: giftCardCodes
          })
          .then((r) => r.data)) as TCalculateAppointment;

        setCalculateAppointment(response);

        if (
          !response.have_stripe_account &&
          response.company_status === "FULL"
        ) {
          setErrorMsg(
            "This salon is not accepting online payments at this time. Please contact the salon directly to book your appointment."
          );
          setDisabledOrderButton(true);
        }
      } catch (err) {
        setErrorMsg(err.response.data.message);
      } finally {
        setRepricing(false);
      }
    })();
    // Re-prices whenever a gift card is added or removed, so the total on the
    // button is always the total the server would charge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBookAppointmentBody, giftCardCodes, isPaymentSuccess]);

  const onLogout = async () => {
    try {
      await api.post("v1/users/impersonate-logout");

      const adminUrl = process.env.REACT_APP_ADMIN_URL;
      window.location.href = `${adminUrl}/dashboard/Customer/${user?.impersonate_id}/show`;
    } catch (error) {
      throw new Error("Error logging out: " + error.message);
    }
  };

  return (
    <div className={baseClassName}>
      <Mobile>
        {errorMsg && (
          <Toast
            msg={errorMsg}
            onClose={() => setErrorMsg("")}
            type="error"
            showIcon
          />
        )}
      </Mobile>

      <div className={bem("content")}>
        <h2 className={bem("title")}>Reservation</h2>
        <div className={bem("message")}>{description}</div>

        <div className={bem("category-type")}>
          <span className={bem("category-title")}>
            Available payment method
          </span>
          <div className={bem("options-container")}>
            {getAvailablePaymentType(
              cart.location?.company,
              calculateAppointment
            )}
          </div>
          <span
            className={bem("cancellation-policy")}
            aria-hidden="true"
            onClick={() => {
              setShowTextModal(true);
              setTextModalData({
                title: "Refund & Cancellation",
                text: getRefundAndCancellationText(cart.location?.company)
              });
            }}
          >
            Cancellation & No-Show Policy Details
          </span>
        </div>

        <div className={bem("category-type")}>
          <span className={bem("category-title")}>Pay with:</span>
          <div
            className={joinClassNames(
              bem("options-container"),
              bem("payment-methods")
            )}
          >
            {cards.map((card: StripePaymentMethodFragment) => (
              <CreditCard
                key={card.id}
                card={card}
                idDefault={card.is_default}
                onClick={() => selectPaymentMethodId(card)}
                checked={paymentMethodId === card.object_json.id}
                cyId="saved-credit-card"
              />
            ))}
          </div>
          <div className={bem("new-card-container")}>
            {addNewCardField ? (
              <form id="addNewCardForm" onSubmit={handlePayButtonClick}>
                <CardElement
                  onChange={(e) => {
                    if (e.complete) {
                      setErrorMsg("");
                    }
                  }}
                  options={CARD_ELEMENT_OPTIONS}
                />
              </form>
            ) : (
              <Button
                className={joinClassNames("flexible", bem("add-card-btn"))}
                onClick={onAddCardClick}
                color={COLORS.SECONDARY}
                variant={VARIANTS.OUTLINED}
                iconLeftName="plus"
                cyId="add-credit-card-button"
                text="Add credit card"
              />
            )}
          </div>
        </div>
        {/*
          Gift cards.

          Shown when a card can actually be spent here and something is being
          charged now. Pay-in-store bookings take no money at this point, so
          there is nothing here for a card to cover - those customers hand the
          code over at the desk instead.

          `giftCardsRedeemable` comes from the server, not from
          gift_cards_enabled or gift_cards_online_enabled. Neither is the right
          question: the first stops new sales but not redemption, and the second
          is only about selling online. What matters here is whether there is
          anything to spend. The server also says no for any salon below the
          Full plan, which is what hides this for Limited and Basic salons.
        */}
        {giftCardsRedeemable &&
          calculateAppointment?.payment_type !== PAYMENT_TYPES.PAY_IN_STORE && (
            <div className={bem("category-type")}>
              <span className={bem("category-title")}>Gift card</span>
              <GiftCardRedeem
                locationId={cart.location?.id}
                codes={giftCardCodes}
                onChange={setGiftCardCodes}
                applied={calculateAppointment?.gift_cards_applied}
                busy={repricing}
                disabled={payingLoading}
              />
              {!!calculateAppointment?.gift_card_total && (
                <>
                  <div className={bem("totals")}>
                    <div className={bem("totals-row")}>
                      <span>Services</span>
                      <span>
                        ${(calculateAppointment.service_price || 0).toFixed(2)}
                      </span>
                    </div>
                    {!!calculateAppointment.amount_paid_now_with_refunds && (
                      <div className={bem("totals-row")}>
                        <span>Salon credit</span>
                        <span>
                          −$
                          {calculateAppointment.amount_paid_now_with_refunds.toFixed(
                            2
                          )}
                        </span>
                      </div>
                    )}
                    {(calculateAppointment.gift_cards_applied || []).map(
                      (card) => (
                        <div className={bem("totals-row")} key={card.code}>
                          <span>Gift card {card.formatted_code.slice(-4)}</span>
                          <span>−${card.amount.toFixed(2)}</span>
                        </div>
                      )
                    )}
                    {!!calculateAppointment.payment_fees && (
                      <div className={bem("totals-row")}>
                        <span>Fees (4%)</span>
                        <span>
                          ${calculateAppointment.payment_fees.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div
                      className={joinClassNames(
                        bem("totals-row"),
                        bem("totals-row-total")
                      )}
                    >
                      <span>Charged to your card</span>
                      <span>
                        $
                        {calculateAppointment.amount_paid_with_payment_method.toFixed(
                          2
                        )}
                      </span>
                    </div>
                  </div>
                  <p className={bem("gift-card-note")}>
                    Gift card payments already applied to a prepaid appointment
                    can cover late cancellation and no-show fees. For
                    pay-in-store appointments, these fees are charged to your
                    card on file and cannot be paid by gift card.
                  </p>
                </>
              )}
            </div>
          )}
        {/* <WavyLine className={bem('wavy-line')} /> */}
        <div className={bem("pay-btn-container")}>
          <Button
            type="submit"
            form="addNewCardForm"
            onClick={handlePayButtonClick}
            className={joinClassNames("flexible", bem("pay-btn"))}
            color={COLORS.SECONDARY}
            variant={VARIANTS.FILLED}
            disabled={disabledOrderButton || payingLoading}
            text={
              payingLoading
                ? "Processing payment..."
                : calculateAppointment?.payment_type === "PAY_IN_STORE" ||
                  // Gift cards can cover the whole booking, and "Pay $0.00"
                  // reads like something has gone wrong.
                  calculateAppointment?.amount_paid_with_payment_method === 0
                ? "Book Now"
                : `Pay $${
                    calculateAppointment?.amount_paid_with_payment_method.toFixed(
                      2
                    ) || ""
                  }`
            }
            submitting={payingLoading}
            cyId="pay-button-desktop"
          />
        </div>
        <Desktop>
          {errorMsg && (
            <Toast
              msg={errorMsg}
              onClose={() => setErrorMsg("")}
              type="error"
              showIcon
              rounded
            />
          )}
        </Desktop>
      </div>

      <FormattedTextModal
        show={showTextModal}
        onHide={() => setShowTextModal(false)}
        title={textModalData?.title}
        text={textModalData?.text || ""}
      />
      <BookingConfirmationModal
        show={
          isPaymentSuccess &&
          !!bookedAppointmentResponse?.createdAppointment &&
          !showAddToCalendarModal
        }
        onHide={async () => {
          if (!showAddToCalendarModal) {
            if (user?.impersonate_name) {
              await onLogout();
            } else {
              navigate(APP_ROUTES.ME);
            }
            resetEverything();
          }
        }}
        onViewDetailsClick={resetEverything}
        onAddToCalendarClick={() => setShowAddToCalendarModal(true)}
        appointment={{
          id: bookedAppointmentResponse?.createdAppointment?.id,
          address:
            bookedAppointmentResponse?.createdAppointment?.location
              ?.address_display || "",
          preview:
            bookedAppointmentResponse?.createdAppointment?.location
              ?.cover_photo_url || "",
          slots: [
            {
              available: true,
              time: bookedAppointmentResponse?.createdAppointment?.start_time
                ? getRelativeDateTime(
                    bookedAppointmentResponse?.createdAppointment?.start_time ||
                      "",
                    bookedAppointmentResponse?.createdAppointment?.location
                      ?.timezone
                  )
                : ""
            }
          ],
          title:
            bookedAppointmentResponse?.createdAppointment?.location?.name || "",
          peopleCount,
          servicesCount:
            bookedAppointmentResponse?.createdAppointment?.appointment_services
              ?.length,
          slotType: "primary"
        }}
      />
      {bookedAppointmentResponse?.createdAppointment && (
        // TODO: add timezone
        <AddToCalendarModal
          show={showAddToCalendarModal}
          onHide={() => setShowAddToCalendarModal(false)}
          event={{
            name: `Upcoming Pointment in ${bookedAppointmentResponse?.createdAppointment?.location?.name}`,
            location:
              bookedAppointmentResponse?.createdAppointment?.location
                ?.address_display ||
              `${bookedAppointmentResponse?.createdAppointment?.location?.address_latitude}, ${bookedAppointmentResponse?.createdAppointment?.location?.address_longitude}`,
            description: `${peopleCount} ${
              peopleCount > 1 ? "people" : "person"
            } • ${
              bookedAppointmentResponse?.createdAppointment
                ?.appointment_services?.length
            } service${
              bookedAppointmentResponse?.createdAppointment
                ?.appointment_services &&
              bookedAppointmentResponse?.createdAppointment
                ?.appointment_services?.length > 1
                ? "s"
                : ""
            }`,
            startDate: getDate(
              bookedAppointmentResponse?.createdAppointment?.start_time || "",
              bookedAppointmentResponse?.createdAppointment?.location?.timezone
            ),
            endDate: getDate(
              bookedAppointmentResponse?.createdAppointment?.end_time?.toString() ||
                "",
              bookedAppointmentResponse?.createdAppointment?.location?.timezone
            ),
            startTime: getFormattedTime(
              bookedAppointmentResponse?.createdAppointment?.start_time || "",
              "HH:mm",
              bookedAppointmentResponse?.createdAppointment?.location?.timezone
            ),
            endTime: getFormattedTime(
              bookedAppointmentResponse?.createdAppointment?.end_time?.toString() ||
                "",
              "HH:mm",
              bookedAppointmentResponse?.createdAppointment?.location?.timezone
            ),
            timeZone:
              bookedAppointmentResponse?.createdAppointment?.location
                ?.timezone || "currentBrowser"
          }}
        />
      )}
    </div>
  );
};

export default PaymentSection;
